import { useMemo } from "react";
import { ApolloClient } from "apollo-client";
import { setContext } from "apollo-link-context";
import { createHttpLink } from "apollo-link-http";
import { Auth } from "aws-amplify";
import { isTreezSSOEnabled, useAuth, TokenData } from "auth";
import { createApolloCache, apolloResolvers } from "../config";
import { useLocalStorage } from "base/hooks";

interface UseApolloClientOptions {
    graphqlEndpoint?: string;
}

export const useApolloClient = (options?: UseApolloClientOptions) => {
    const { token, authConfig } = useAuth();
    const [tokenData] = useLocalStorage<TokenData | null>("token", null);

    const client = useMemo(() => {
        // Get GraphQL endpoint from options, authConfig, or fallback to env
        const graphqlUri = isTreezSSOEnabled
            ? options?.graphqlEndpoint ||
              authConfig?.graphqlUrl?.replace(/\/(develop|master)\b/, "/query")
            : process.env.REACT_APP_GRAPHQL_ENDPOINT_URL;

        if (!graphqlUri) {
            console.warn("GraphQL endpoint URL is not configured");
        }

        // Create HTTP link with dynamic URI
        const httpLink = createHttpLink({
            uri: graphqlUri,
        });

        // Create auth link that uses token from AuthContext
        const authLink = setContext(async (_, { headers }) => {
            let authToken = null;

            if (isTreezSSOEnabled) {
                // Use token from AuthContext (provided by AuthProvider)
                if (token) {
                    authToken = `Treez ${token}`;
                } else {
                    // Fallback to localStorage for backward compatibility
                    const treezAccessToken = tokenData?.accessToken;
                    if (treezAccessToken) {
                        authToken = `Treez ${treezAccessToken}`;
                    }
                }
            } else {
                try {
                    const cognitoUser = await Auth.currentAuthenticatedUser();
                    authToken =
                        cognitoUser.signInUserSession.accessToken.jwtToken;
                } catch (error) {
                    // User not authenticated
                    console.warn("Cognito user not authenticated:", error);
                }
            }

            return {
                headers: {
                    ...headers,
                    ...(authToken && { Authorization: authToken }),
                },
            };
        });

        // Create cache
        const cache = createApolloCache();

        // Create Apollo client
        return new ApolloClient({
            link: authLink.concat(httpLink),
            cache,
            resolvers: apolloResolvers,
        });
    }, [token, options?.graphqlEndpoint, authConfig?.graphqlUrl]);

    return client;
};
